import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import toast from "react-hot-toast";
import { FormProvider, useForm } from "react-hook-form";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import { ButtonShadcn } from "@/components/ui/Button";
import RHFSelect from "@/components/form/RHFSelect";

import CandidateStatusService from "@/services/candidateStatusService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { getEligibleReplacementStatuses } from "../statusFlags";

const DeleteCandidateStatusModal = ({
  open,
  onModelClose,
  status,
  flowId,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [replacementOptions, setReplacementOptions] = useState([]);
  const [inlineError, setInlineError] = useState("");
  const [backendNeedsReplacement, setBackendNeedsReplacement] = useState(false);
  // Set when the backend refuses the delete outright and no choice made in this
  // dialog can change that — currently only the compliance-trigger case. Kept
  // separate from `inlineError`, which marks a *recoverable* problem (pick a
  // replacement and retry): here retrying is pointless, so the Delete button
  // goes away rather than inviting a second identical failure.

  const methods = useForm();
  const { watch, reset } = methods;
  const replacementStatusId = watch("replacementStatusId");

  const isInitial = !!status?.isInitial;
  const inUseCount = Number(status?.inUseCount ?? 0);
  const needsReplacement = isInitial || inUseCount > 0 || backendNeedsReplacement;

  // Terminal statuses are filtered out for every replacement — the server
  // refuses Hired/Rejected as a receiver (INVALID_REPLACEMENT_STATUS) — and
  // reusable waypoints drop out too when the new status must be initial.
  const eligibleOptions = useMemo(
    () =>
      getEligibleReplacementStatuses(replacementOptions, {
        excludeId: status?.id,
        forInitial: isInitial,
      }),
    [replacementOptions, status?.id, isInitial],
  );

  useEffect(() => {
    if (!open) {
      reset({ replacementStatusId: "" });
      setLoadingOptions(false);
      setReplacementOptions([]);
      setInlineError("");
      setLoading(false);
      setBackendNeedsReplacement(false);
    }
  }, [open, reset]);

  useEffect(() => {
    const fetchOptions = async () => {
      if (!open || !needsReplacement || !flowId) return;

      try {
        setLoadingOptions(true);
        const res = await CandidateStatusService.list({
          flowId,
          page: 1,
          per_page: 100,
          sort_by: "name",
          sort_order: "asc",
        });

        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.data) ? res.data.data : [];

        setReplacementOptions(list.filter((item) => item?.id !== status?.id));
      } catch (e) {
        toast.error(apiErrorMessageFormat(e, "Failed to load replacement statuses"));
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [open, needsReplacement, flowId, status?.id]);

  const handleDelete = async () => {
    try {
      if (needsReplacement && !replacementStatusId) {
        setInlineError(isInitial ? "Select the status that will become the new initial status." : "Select a replacement status to reassign candidates.");
        return;
      }

      setLoading(true);
      setInlineError("");
      await onConfirm(needsReplacement ? replacementStatusId : null);
    } catch (e) {
      const errorCode = e?.response?.data?.error_code || e?.response?.data?.errorCode;

      if (errorCode === "STATUS_IN_USE") {
        setBackendNeedsReplacement(true);
        setInlineError(apiErrorMessageFormat(e, "This status is in use — select a replacement status to continue."));
        return;
      }

      if (errorCode === "INVALID_REPLACEMENT_STATUS") {
        setInlineError(apiErrorMessageFormat(e, "Hired and rejected statuses cannot receive candidates — choose another status."));
        return;
      }

      setInlineError(apiErrorMessageFormat(e, "Failed to delete status"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onModelClose?.()}>
      <DialogContent className="w-[520px] md:w-full flex flex-col p-0">
        <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
          <div>
            <DialogTitle className="text-[15px] font-semibold text-text-primary">Delete Candidate Status</DialogTitle>

            <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">This action soft-deletes the selected status.</DialogDescription>
          </div>

          <DialogClose className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        <div className="space-y-4 p-5">
          <div className="rounded-2xl border border-border-primary bg-background-secondary p-4">
            <div className="flex gap-3">
              <div className="mt-0.5 text-destructive">
                <AlertTriangle size={18} />
              </div>

              <div>
                <p className="text-sm font-medium text-text-primary">
                  Are you sure you want to delete <span className="font-semibold">{status?.name || "this status"}</span>?
                </p>
                <p className="mt-1 text-xs text-text-secondary">
                  {isInitial
                    ? "The initial status needs a replacement — the selected status becomes the new initial status at step 1, and candidates currently on this status are moved to it."
                    : needsReplacement
                      ? "This status is assigned to candidates. Select a replacement status to move them to before deleting."
                      : "Deleted statuses are removed from the pipeline. This action cannot be undone."}
                </p>
              </div>
            </div>
          </div>

          {needsReplacement && (
            <div className="rounded-2xl border border-border-primary bg-background-form p-4">
              <FormProvider {...methods}>
                <RHFSelect
                  name="replacementStatusId"
                  label={isInitial ? "New Initial Status" : "Replacement Status"}
                  placeholder={loadingOptions ? "Loading statuses..." : "Select replacement status"}
                  options={eligibleOptions.map((item) => ({
                    label: `${item?.name} (${item?.code})`,
                    value: item?.id,
                  }))}
                  required
                />
              </FormProvider>

              {!loadingOptions && eligibleOptions.length === 0 ? (
                <p className="mt-2 text-xs text-text-secondary">No eligible statuses to replace this one. Create a new status first, then try again.</p>
              ) : null}

            </div>
          )}

          {inlineError ? (
            <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">{inlineError}</div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border-primary bg-background-secondary">
          <ButtonShadcn type="button" variant="outline" size="lg" onClick={onModelClose} className="rounded-full px-5">
            Cancel
          </ButtonShadcn>

          <ButtonShadcn
            type="button"
            variant="destructive"
            size="lg"
            disabled={loading}
            onClick={handleDelete}
            className="rounded-full px-5 border border-destructive/20"
          >
            {loading ? "Deleting..." : "Delete Status"}
          </ButtonShadcn>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteCandidateStatusModal;
